/**
 * Transferir exames aplica na hora, sem o painel — compartilhado pela Aplicação de Exames e pela
 * Migração de Riscos e Exames.
 *
 * Cada exame vai obrigatório, sem datas nem observações, com a periodicidade do próprio cadastro.
 * Lote com mais de um exame, com exame escondido pela busca ou vindo de "aplicar todos" pede
 * confirmação antes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import type { ContextoDaAplicacao } from '../../components/exam-applications/DualTransferList';
import type { ApplicationLevel, AvailableExam, ExamWithApplication } from '../../types/examApplication';
import { parseFrequencyToMonths } from '../../utils/examFrequency';
import type { useExamApplications } from './hooks/useExamApplications';

type GanchoDeExames = ReturnType<typeof useExamApplications>;

export type GravidadeDoAviso = 'success' | 'warning' | 'error';

const NOMES_NA_CONFIRMACAO = 5;

/** "A, B e C" — ou os primeiros nomes e "e mais N" quando a lista é longa. */
function listarNomesDeExames(nomes: string[]): string {
  if (nomes.length > NOMES_NA_CONFIRMACAO) {
    return `${nomes.slice(0, NOMES_NA_CONFIRMACAO).join(', ')} e mais ${nomes.length - NOMES_NA_CONFIRMACAO}`;
  }
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

function avisarEscondidos(total: number, escondidos: number): string {
  if (escondidos === 0) return '';
  if (total === 1) return ' A busca está escondendo este exame.';
  return ` A busca está escondendo ${escondidos} deles.`;
}

/** O que o gancho mostrava no render em que ficou ocioso. */
interface EstadoDoGancho {
  error: string | null;
  applied: ExamWithApplication[];
}

interface OpcoesDeAplicarDireto {
  nivel: ApplicationLevel;
  alvoId: number | null;
  loading: boolean;
  error: string | null;
  applied: ExamWithApplication[];
  applyMultipleExams: GanchoDeExames['applyMultipleExams'];
  loadExams: GanchoDeExames['loadExams'];
  avisar: (mensagem: string, gravidade: GravidadeDoAviso) => void;
}

export function useAplicarExamesDireto({
  nivel,
  alvoId,
  loading,
  error,
  applied,
  applyMultipleExams,
  loadExams,
  avisar,
}: OpcoesDeAplicarDireto) {
  // Enquanto transferir aplica, a lista continua na tela (travada) em vez de dar lugar ao carregando.
  const [aplicando, setAplicando] = useState(false);

  // Confirmação antes de aplicar: guarda os nomes, quantos a busca esconde e quem espera a resposta.
  const [confirmacao, setConfirmacao] = useState<{
    aberta: boolean;
    nomes: string[];
    escondidos: number;
    responder: (prosseguir: boolean) => void;
  } | null>(null);

  // O resultado de uma chamada do gancho (o erro, a lista recarregada) só aparece no render
  // seguinte, e a chamada seguinte o apagaria antes disso. Quem precisa dele registra a espera
  // antes da chamada e é avisado no primeiro render em que o gancho fica ocioso.
  // Quem sai da tela no meio da espera recebe null: o envio termina sem esperar e sem avisar.
  const avisarQuandoOciosoRef = useRef<((estado: EstadoDoGancho | null) => void) | null>(null);
  const montadaRef = useRef(true);
  useEffect(() => {
    const avisarOcioso = avisarQuandoOciosoRef.current;
    if (loading || !avisarOcioso) return;
    avisarQuandoOciosoRef.current = null;
    avisarOcioso({ error, applied });
  });
  useEffect(() => {
    montadaRef.current = true;
    return () => {
      montadaRef.current = false;
      const avisarOcioso = avisarQuandoOciosoRef.current;
      avisarQuandoOciosoRef.current = null;
      avisarOcioso?.(null);
    };
  }, []);
  const esperarGanchoOcioso = useCallback(
    () =>
      new Promise<EstadoDoGancho | null>((resolve) => {
        if (!montadaRef.current) {
          resolve(null);
          return;
        }
        avisarQuandoOciosoRef.current = resolve;
      }),
    []
  );

  // O envio em lote grava um valor só para todos os exames da chamada, então vai uma chamada por periodicidade.
  const aplicar = useCallback(
    async (items: (AvailableExam | ExamWithApplication)[]) => {
      if (!alvoId) return;

      const availableItems = items.filter(
        (item): item is AvailableExam => !('applicationLevel' in item)
      );

      if (availableItems.length === 0) return;

      const examIdsPorPeriodicidade = new Map<number | null, number[]>();
      for (const exam of availableItems) {
        const periodicityMonths = parseFrequencyToMonths(exam.defaultFrequency);
        const grupo = examIdsPorPeriodicidade.get(periodicityMonths) ?? [];
        grupo.push(Number(exam.id));
        examIdsPorPeriodicidade.set(periodicityMonths, grupo);
      }
      const semPeriodicidade = examIdsPorPeriodicidade.get(null) ?? [];

      setAplicando(true);
      try {
        let algumGrupoFalhou = false;
        let motivo: string | null = null;
        for (const [periodicityMonths, examIds] of examIdsPorPeriodicidade) {
          const ocioso = esperarGanchoOcioso();
          const success = await applyMultipleExams(nivel, alvoId, examIds, {
            periodicityMonths,
            isRequired: true,
          });
          if (!success) {
            algumGrupoFalhou = true;
            motivo ??= (await ocioso)?.error ?? null;
          }
        }
        if (!montadaRef.current) return;

        const avisarSemPeriodicidade = (quantos: number) =>
          quantos > 0
            ? ` ${quantos} ficou(aram) sem periodicidade: ajuste pela engrenagem do exame aplicado.`
            : '';

        const avisarAplicadosSemFalha = (quantosSemPeriodicidade: number) => {
          const avisoDePeriodicidade = avisarSemPeriodicidade(quantosSemPeriodicidade);
          avisar(
            avisoDePeriodicidade
              ? `${availableItems.length} exame(s) aplicado(s).${avisoDePeriodicidade}`
              : `${availableItems.length} exame(s) aplicado(s) com sucesso`,
            avisoDePeriodicidade ? 'warning' : 'success'
          );
        };

        if (!algumGrupoFalhou) {
          avisarAplicadosSemFalha(semPeriodicidade.length);
          return;
        }

        // O envio em lote para no primeiro exame que falha, com parte do grupo já gravada: só a
        // lista recarregada diz quantos dos exames transferidos ficaram aplicados.
        const recarregada = esperarGanchoOcioso();
        await loadExams(nivel, alvoId);
        const estadoRecarregado = await recarregada;
        if (!estadoRecarregado) return;
        const examIdsAplicados = new Set(estadoRecarregado.applied.map((exam) => Number(exam.examId)));
        const aplicados = availableItems.filter((exam) => examIdsAplicados.has(Number(exam.id))).length;
        const falharam = availableItems.length - aplicados;
        const semPeriodicidadeAplicados = semPeriodicidade.filter((examId) => examIdsAplicados.has(examId)).length;
        // O servidor respondeu erro, mas a lista recarregada mostra todos aplicados: não houve falha a contar.
        if (falharam === 0) {
          avisarAplicadosSemFalha(semPeriodicidadeAplicados);
          return;
        }
        const avisoDePeriodicidade = avisarSemPeriodicidade(semPeriodicidadeAplicados);
        avisar(
          `${aplicados} exame(s) aplicado(s); ${falharam} não pôde(puderam) ser aplicado(s)` +
            (motivo ? `: ${motivo}` : '') +
            (avisoDePeriodicidade ? `.${avisoDePeriodicidade}` : ''),
          'error'
        );
      } finally {
        if (montadaRef.current) setAplicando(false);
      }
    },
    [alvoId, nivel, applyMultipleExams, loadExams, esperarGanchoOcioso, avisar]
  );

  // Um exame visível marcado segue direto. Pede confirmação quando são vários, quando a busca esconde
  // algum deles ou quando veio de "aplicar todos" — que leva também o que a busca não mostra.
  const confirmarAntesDeAplicar = useCallback(
    (items: (AvailableExam | ExamWithApplication)[], contexto?: ContextoDaAplicacao) => {
      const nomes = items.filter((item) => !('applicationLevel' in item)).map((item) => item.examName);
      const escondidos = contexto?.escondidosPelaBusca ?? 0;
      if (nomes.length === 0) return Promise.resolve(true);
      if (nomes.length === 1 && !contexto?.todos && escondidos === 0) return Promise.resolve(true);
      return new Promise<boolean>((responder) =>
        setConfirmacao({ aberta: true, nomes, escondidos, responder })
      );
    },
    []
  );

  const responderConfirmacao = (prosseguir: boolean) => {
    confirmacao?.responder(prosseguir);
    // Fecha mantendo os nomes, para o texto não sumir durante a animação de saída.
    setConfirmacao((atual) => atual && { ...atual, aberta: false });
  };

  const quantos = confirmacao?.nomes.length ?? 0;
  const dialogoDeConfirmacao = (
    <Dialog
      open={confirmacao?.aberta ?? false}
      onClose={() => responderConfirmacao(false)}
      aria-labelledby="confirmar-aplicar-titulo"
    >
      <DialogTitle id="confirmar-aplicar-titulo">
        {quantos === 1
          ? 'Aplicar 1 exame como obrigatório?'
          : `Aplicar ${quantos} exames como obrigatórios?`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {listarNomesDeExames(confirmacao?.nomes ?? [])}.{' '}
          {quantos === 1 ? 'Entra' : 'Cada um entra'} com a periodicidade do próprio cadastro.
          {avisarEscondidos(quantos, confirmacao?.escondidos ?? 0)}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => responderConfirmacao(false)}>Cancelar</Button>
        <Button variant="contained" onClick={() => responderConfirmacao(true)} autoFocus>
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { aplicando, aplicar, confirmarAntesDeAplicar, dialogoDeConfirmacao };
}
